import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import {
  Flex,
  FlexCenter,
  FlexColumnCenter,
  RadioField,
  SelectField as BaseSelectField,
  TimeField
} from '@ubeya/shared-web/components';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { required } from '@ubeya/shared/utils/form';
import { totalDuration } from 'scheduling/utils';
import { AVAILABILITY_REQUEST_TYPE } from 'scheduling/constants';

const ExtraInfo = styled(FlexCenter)`
  align-self: center;
  margin-bottom: 38px;
  margin-top: 10px;
`;

const Dash = styled.h3`
  margin: 0 10px;
  font-size: 20px;
`;

const Container = styled(FlexColumnCenter)`
  flex: 1;
  padding-top: 20px;
  padding-bottom: 20px;
  justify-content: flex-start;
`;

const CopyToContainer = styled(Flex)`
  align-self: center;
  justify-content: center;
  margin-bottom: 15px;
`;

const DaysContainer = styled(Flex)`
  flex-wrap: wrap;
  justify-content: flex-start;
  align-self: center;
`;

const Day = styled(FlexCenter)`
  border: ${({ isDisabled }) => (isDisabled ? 3 : 1)}px solid
    ${({ theme, isDisabled }) => (isDisabled ? theme.colors.disabled : theme.colors.primary)};
  cursor: pointer;
  border-radius: 6px;
  background-color: ${({ theme, selected, isDisabled }) =>
    selected ? theme.colors.primary : isDisabled ? theme.colors.disabled : theme.colors.white};
  height: 40px;
  width: 40px;
  margin: 7px;
`;

const DayTitle = styled.div`
  font-weight: 500;
  font-size: 11px;
  color: ${({ theme, selected }) => (!selected ? theme.colors.primary : theme.colors.white)};
`;

const CopyToTitle = styled.div`
  margin-top: 15px;
  margin-right: 15px;
`;

const Strip = styled.div`
  height: 2px;
  width: 100%;
  background-color: rgba(37, 37, 37, 0.1);
`;

const SummaryAll = styled.h4`
  flex: 1;
  margin-left: 10px;
`;

const SummaryContainer = styled.div`
  margin-top: 10px;
  align-items: start;
  min-width: 400px;
`;

const SummaryTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 20px;
`;

const SummaryItem = styled(FlexCenter)`
  margin-bottom: 10px;
`;
const SummaryStrip = styled.div`
  height: 26px;
  width: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.primary};
`;

const SummaryTimes = styled.div`
  font-weight: 400;
  margin-left: 10px;
  margin-right: 10px;
`;

const SummaryTotal = styled.div`
  font-weight: 300;
  color: ${({ theme }) => theme.colors.grey2}
  margin-left: 10px;
`;
const SummaryItems = styled(FlexColumnCenter)`
  align-items: start;
  margin-bottom: 10px;
`;

const SelectField = styled(BaseSelectField)`
  width: 200px;
`;

const TIME_PICKER_INTERVAL = 30;

const AbsenceSummary = ({ t, date, absenceType, mappedTimeOffRequestOptions }) => {
  if (!absenceType) {
    return <></>;
  }
  return (
    <SummaryContainer>
      <SummaryTitle>{t('timeAvailabilitySummary')}</SummaryTitle>
      <SummaryItems>
        <SummaryItem>
          <SummaryStrip />
          <SummaryTimes>{moment(date).format('dddd')}</SummaryTimes>
          <SummaryTotal>{mappedTimeOffRequestOptions[absenceType]?.slug || ''}</SummaryTotal>
        </SummaryItem>
      </SummaryItems>
    </SummaryContainer>
  );
};

const Summary = ({ t, dates, endTime, startTime }) => (
  <SummaryContainer>
    <SummaryTitle>{t('timeAvailabilitySummary')}</SummaryTitle>
    <SummaryItems>
      {dates.map((date, idx) => (
        <SummaryItem key={idx}>
          <SummaryStrip />
          <SummaryTimes>
            {moment(date).format('dddd')} {moment(startTime).format('HH:mm')}-{moment(endTime).format('HH:mm')}
          </SummaryTimes>
          <SummaryTotal>
            ({totalDuration(moment(startTime), moment(endTime)).asHours().toFixed(1)} {t('hours')})
          </SummaryTotal>
        </SummaryItem>
      ))}
    </SummaryItems>
  </SummaryContainer>
);

export const AddTimeAvailabilityModalFields = ({
  t,
  valid,
  values,
  selectedDays,
  setSelectedDays,
  timePickerOptions,
  getSelectedDates,
  mappedTimeOffRequestOptions,
  timeOffRequestOptions,
  selectedDate,
  daysOptions
}) => {
  const {
    input: { value: availabilityOption, onChange: onChangeAvailabilityOptionId }
  } = useField('status');

  const {
    input: { onChange: onChangeAbsenceType }
  } = useField('absenceType');

  const onChangeAvailabilityOption = (id) => {
    onChangeAvailabilityOptionId(id);

    if (id !== AVAILABILITY_REQUEST_TYPE.TIME_OFF) {
      onChangeAbsenceType(null);
    }
  };

  return (
    <Container>
      <RadioField
        name="status"
        onChange={onChangeAvailabilityOption}
        options={[
          { value: AVAILABILITY_REQUEST_TYPE.PREFER, label: t('preferToWork') },
          { value: AVAILABILITY_REQUEST_TYPE.PREFER_NOT, label: t('preferNotToWork') },
          { value: AVAILABILITY_REQUEST_TYPE.TIME_OFF, label: t('timeOff') }
        ]}
      />
      <ExtraInfo>
        {availabilityOption === AVAILABILITY_REQUEST_TYPE.TIME_OFF ? (
          <SelectField
            name="absenceType"
            options={timeOffRequestOptions}
            isMulti={false}
            validate={availabilityOption === AVAILABILITY_REQUEST_TYPE.TIME_OFF ? required : undefined}
          />
        ) : (
          <>
            <TimeField
              interval={TIME_PICKER_INTERVAL}
              title={t('from')}
              name="startTime"
              defaultOptions={timePickerOptions}
              required
            />
            <Dash />
            <TimeField
              interval={TIME_PICKER_INTERVAL}
              title={t('to')}
              name="endTime"
              defaultOptions={timePickerOptions}
              required
            />
          </>
        )}
      </ExtraInfo>
      {availabilityOption !== AVAILABILITY_REQUEST_TYPE.TIME_OFF && (
        <CopyToContainer>
          <CopyToTitle>{t('copyTo')}:</CopyToTitle>
          <DaysContainer>
            {(daysOptions || []).map(({ momentDate, date, day }, idx) => {
              const isDisabled = momentDate.isSame(moment(selectedDate, API_DATE_FORMAT), 'date');

              return (
                <Day
                  key={idx}
                  selected={selectedDays[date]}
                  isDisabled={isDisabled}
                  onClick={() => {
                    setSelectedDays((prev) => ({
                      ...prev,
                      [date]: !prev[date]
                    }));
                  }}>
                  <DayTitle selected={selectedDays[date]}>{day.toUpperCase()}</DayTitle>
                </Day>
              );
            })}
          </DaysContainer>
        </CopyToContainer>
      )}
      <Strip />
      <SummaryContainer>
        {availabilityOption === AVAILABILITY_REQUEST_TYPE.TIME_OFF ? (
          <AbsenceSummary
            t={t}
            {...values}
            date={selectedDate}
            mappedTimeOffRequestOptions={mappedTimeOffRequestOptions}
          />
        ) : (
          <>
            <Summary t={t} dates={getSelectedDates()} {...values} />

            <SummaryAll>
              {(!valid
                ? 0
                : (
                    totalDuration(moment(values.startTime), moment(values.endTime)).asHours() *
                    Object.values(selectedDays).filter((v) => !!v).length
                  ).toFixed(1)
              ).toString()}{' '}
              {t('hours')}
            </SummaryAll>
          </>
        )}
      </SummaryContainer>
    </Container>
  );
};
